import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  ChevronRight,
  FileText,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Search,
  Settings2,
  type LucideProps,
} from 'lucide-react';
import { useThemeStore, isDarkChromeTheme } from '@/entities/session/model/themeStore';
import { useSessionStore } from '@/entities/session';
import { useOrgAccess } from '@/shared/lib/useOrgAccess';
import { BrandWordmark } from '@/shared/ui';

type NavIcon = ComponentType<LucideProps>;

type NavLeaf = { to: string; labelKey: string; icon?: NavIcon; end?: boolean };
type NavGroup = {
  kind: 'group';
  id: string;
  labelKey: string;
  icon: NavIcon;
  collapsible?: boolean;
  /** When set, group title navigates here (chevron still toggles). */
  to?: string;
  children: NavLeaf[];
};
type NavItem = { kind: 'link'; to: string; labelKey: string; icon: NavIcon; end?: boolean };
type NavEntry = NavGroup | NavItem;

const ORG_NAV: NavItem = {
  kind: 'link',
  to: '/app/organizations',
  labelKey: 'nav.organizations',
  icon: Building2,
};
const COLLAPSE_STORAGE_KEY = 'tenderts.sidebar.collapsed';

function NavGlyph({ icon: Icon }: { icon: NavIcon }) {
  return <Icon className="h-[1.125rem] w-[1.125rem] shrink-0 opacity-90" strokeWidth={1.75} aria-hidden />;
}

function readCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function buildBaseNav(orgEnabled: boolean, isAdmin: boolean): NavEntry[] {
  const nav: NavEntry[] = [
    { kind: 'link', to: '/app/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
    { kind: 'link', to: '/app/tenders/reports', labelKey: 'nav.tenderReports', icon: FileText },
    { kind: 'link', to: '/app/tenders/board', labelKey: 'nav.tenderBoard', icon: LayoutGrid },
  ];
  if (orgEnabled) nav.push(ORG_NAV);

  const matchingChildren: NavLeaf[] = [
    { to: '/app/tenders/search', labelKey: 'nav.tenderSearch' },
    // Hidden for now: saved-searches
    { to: '/app/tenders/schedules', labelKey: 'nav.tenderSchedules' },
    { to: '/app/profile/edit', labelKey: 'nav.tenderMatchingParams' },
  ];

  const companyProfileChildren: NavLeaf[] = [
    { to: '/app/profile/team', labelKey: 'nav.companyTeam' },
    { to: '/app/activity', labelKey: 'nav.statistics' },
    // Hidden for now: archive placeholder
    { to: '/app/referral', labelKey: 'nav.referral' },
  ];

  const cabinetMgmtChildren: NavLeaf[] = [
    { to: '/app/tenders/tariff', labelKey: 'nav.billing' },
    { to: '/app/tenders/orders', labelKey: 'nav.tenderOrders' },
    // Hidden for now: cabinet agent
    { to: '/app/settings', labelKey: 'nav.settings' },
    { to: '/app/cabinet/activity', labelKey: 'nav.cabinetActivity' },
  ];
  if (isAdmin) {
    cabinetMgmtChildren.push(
      { to: '/app/admin/telegram', labelKey: 'nav.adminTelegram' },
      { to: '/app/admin/users', labelKey: 'nav.adminUsers' },
      { to: '/app/admin/tariffs', labelKey: 'nav.adminTariffs' },
      { to: '/app/admin/services', labelKey: 'nav.adminServices' },
      { to: '/app/admin/roles', labelKey: 'nav.adminRoles' },
    );
  }

  nav.push(
    {
      kind: 'group',
      id: 'matching',
      labelKey: 'nav.tenderMatching',
      icon: Search,
      collapsible: true,
      children: matchingChildren,
    },
    {
      kind: 'group',
      id: 'companyProfile',
      labelKey: 'nav.companyProfile',
      icon: Building2,
      collapsible: true,
      to: '/app/profile',
      children: companyProfileChildren,
    },
    {
      kind: 'group',
      id: 'cabinet',
      labelKey: 'nav.cabinetManage',
      icon: Settings2,
      collapsible: true,
      children: cabinetMgmtChildren,
    },
  );
  return nav;
}

function groupHasActiveChild(group: NavGroup, pathname: string): boolean {
  if (group.to && (pathname === group.to || pathname.startsWith(`${group.to}/`))) {
    return true;
  }
  return group.children.some(
    (child) => pathname === child.to || pathname.startsWith(`${child.to}/`),
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const isDark = isDarkChromeTheme(theme);
  const logoutSession = useSessionStore((s) => s.logout);
  const isAdmin = useSessionStore((s) => s.platformRole === 'ADMIN');
  const { enabled: orgEnabled } = useOrgAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const baseNav = useMemo(() => buildBaseNav(orgEnabled, isAdmin), [orgEnabled, isAdmin]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    setCollapsed((prev) => {
      let next = prev;
      for (const item of baseNav) {
        if (item.kind !== 'group' || !item.collapsible) continue;
        if (groupHasActiveChild(item, location.pathname) && prev[item.id]) {
          if (next === prev) next = { ...prev };
          next[item.id] = false;
        }
      }
      return next;
    });
  }, [baseNav, location.pathname]);

  const handleLogout = () => {
    logoutSession();
    navigate('/login', { replace: true });
  };

  const toggleGroup = (id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const linkClass = ({ isActive }: { isActive: boolean }, indent = false) => {
    const base = `flex items-center gap-3 rounded-lg ${indent ? 'pl-9 pr-3' : 'px-3'} py-2 text-sm transition-colors`;
    if (isActive) {
      if (isDark) {
        return `${base} nav-active-gradient font-medium shadow-emerald`;
      }
      return `${base} bg-primary-50 font-medium text-primary-700`;
    }
    return base + (isDark
      ? ' text-[var(--t-sidebar-text)] hover:text-white hover:bg-white/5'
      : ' text-text-secondary hover:bg-surface-tertiary hover:text-text-primary');
  };

  const groupHeaderClass = (active: boolean) => {
    const base = 'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors text-left';
    if (active) {
      return isDark
        ? `${base} text-white`
        : `${base} font-medium text-primary-700`;
    }
    return base + (isDark
      ? ' text-[var(--t-sidebar-text)] hover:text-white hover:bg-white/5'
      : ' text-text-secondary hover:bg-surface-tertiary hover:text-text-primary');
  };

  return (
    <aside
      className="sidebar-themed flex w-60 flex-col border-r transition-colors"
      style={{
        background: 'var(--t-sidebar-bg)',
        borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'var(--t-border)',
      }}
    >
      <div className="sidebar-brand flex h-16 items-center px-6">
        <BrandWordmark
          to="/app"
          heightClass="h-8"
          className="sidebar-logo"
          onDarkSurface={isDark}
        />
      </div>

      <nav className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto px-3 pb-4">
        {baseNav.map((item) => {
          if ('kind' in item && item.kind === 'group') {
            const childActive = groupHasActiveChild(item, location.pathname);
            const isOpen = !collapsed[item.id];

            return (
              <div key={item.id} className="mt-1">
                <div className="flex items-center gap-0.5">
                  {item.to ? (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `${groupHeaderClass(isActive || childActive)} flex-1 min-w-0`
                      }
                    >
                      <NavGlyph icon={item.icon} />
                      <span className="flex-1 truncate">{t(item.labelKey)}</span>
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleGroup(item.id)}
                      aria-expanded={isOpen}
                      className={`${groupHeaderClass(childActive)} flex-1 min-w-0`}
                    >
                      <NavGlyph icon={item.icon} />
                      <span className="flex-1 truncate text-left">{t(item.labelKey)}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.id)}
                    aria-expanded={isOpen}
                    aria-label={t(item.labelKey)}
                    className={`shrink-0 rounded-lg px-2 py-2 text-sm transition-colors ${
                      isDark
                        ? 'text-[var(--t-sidebar-text)] hover:text-white hover:bg-white/5'
                        : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                    }`}
                  >
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                      strokeWidth={1.75}
                      aria-hidden
                    />
                  </button>
                </div>
                {isOpen && (
                  <div className="mt-0.5 flex flex-col gap-0.5">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        className={(s) => linkClass(s, true)}
                      >
                        {t(child.labelKey)}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          const leaf = item as NavItem;
          return (
            <NavLink
              key={leaf.to}
              to={leaf.to}
              end={leaf.end}
              className={(s) => linkClass(s)}
            >
              <NavGlyph icon={leaf.icon} />
              <span className="truncate">{t(leaf.labelKey)}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 pb-4">
        <button
          type="button"
          onClick={handleLogout}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
            isDark
              ? 'text-[var(--t-sidebar-text)] hover:text-white hover:bg-white/10'
              : 'text-text-secondary hover:bg-red-50 hover:text-red-600'
          }`}
        >
          <NavGlyph icon={LogOut} />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
}
